import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CARE_STAFF_ROLES, viewForPath, type Role, type ViewId } from '@aithora/shared';
import { useConsoleRole } from '../session/SessionContext';
import { sx } from '../ui/designStyles';

/**
 * "Pick up where you left off" (V9 hand-off, design rsShow L844 and handoff.js resume):
 * after a sign-out or an interruption, a nurse or the Facility Admin goes back to the same
 * resident's chart or Worklist tab. The line is small, sits below the emergency banner, and
 * shows only on the page it refers to. "Go back" opens the place; "Not now" hides it until
 * the next sign-in.
 *
 * Pages record where the person is with useResumePoint (the Residents chart, a Worklist
 * tab). Kept in this browser per role (localStorage aithora.v9.resume.c.{role}, as the
 * design): a convenience, never the record.
 */
export interface ResumePoint {
  /** The page the point belongs to; the line shows only there. */
  view: ViewId;
  /** Where Go back goes: a console path with its query ("/residents?resident=…"). */
  to: string;
  /** What the line names: "Beatrice Okoro’s chart", "Worklist · Med pass". */
  label: string;
}

const KEY = (role: Role) => `aithora.v9.resume.c.${role}`;
/** Who gets the line (design: Care Staff and Facility Admin). */
export const resumeRole = (role: Role | null): role is Role => role === 'ADMIN' || (role !== null && CARE_STAFF_ROLES.has(role));

export function readResume(role: Role): ResumePoint | null {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY(role)) ?? 'null') as Partial<ResumePoint> | null;
    return v && typeof v.view === 'string' && typeof v.to === 'string' && typeof v.label === 'string' && v.label ? (v as ResumePoint) : null;
  } catch {
    return null;
  }
}

export function writeResume(role: Role, p: ResumePoint): void {
  try {
    window.localStorage.setItem(KEY(role), JSON.stringify({ view: p.view, to: p.to, label: p.label }));
  } catch {
    /* storage off: the line simply does not come back */
  }
}

/** The design's line (handoff.js resume.line). */
export const resumeLine = (p: ResumePoint) => `Pick up where you left off: ${p.label}`;

/** The place the page is showing now, so a later visit can come back to it. Null: nowhere worth coming back to. */
const CurrentCtx = { current: null as ResumePoint | null, listeners: new Set<() => void>() };

/**
 * A page records the place on screen (a resident's chart, a Worklist tab). Pass null when
 * nothing specific is open.
 */
export function useResumePoint(point: ResumePoint | null): void {
  const { role, viewingAs } = useConsoleRole();
  const key = point ? `${point.view}|${point.to}|${point.label}` : '';
  useEffect(() => {
    CurrentCtx.current = point;
    CurrentCtx.listeners.forEach((l) => l());
    // Never written while viewing as another role: that is not this person's place.
    if (point && !viewingAs && resumeRole(role)) writeResume(role, point);
    return () => {
      CurrentCtx.current = null;
      CurrentCtx.listeners.forEach((l) => l());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, role, viewingAs]);
}

export function ResumeLine() {
  const { role, viewingAs } = useConsoleRole();
  const location = useLocation();
  const navigate = useNavigate();
  // The place saved before this visit, read once per role (design _rsPrev).
  const prev = useRef<{ role: Role | null; point: ResumePoint | null } | null>(null);
  if (!prev.current || prev.current.role !== role) prev.current = { role, point: resumeRole(role) && !viewingAs ? readResume(role) : null };
  const [gone, setGone] = useState(false);
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    CurrentCtx.listeners.add(l);
    return () => {
      CurrentCtx.listeners.delete(l);
    };
  }, []);
  const p = prev.current.point;
  const view = viewForPath(location.pathname);
  const cur = CurrentCtx.current;
  if (gone || !p || !view || p.view !== view || (cur && cur.label === p.label)) return null;
  return (
    <div
      data-screen-label="Pick up where you left off"
      data-testid="resume-line"
      style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;margin-bottom:10px;padding:4px 10px;border-radius:8px;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);min-width:0;')}
    >
      <span style={sx('flex:1 1 260px;min-width:0;font-size:14px;font-weight:700;color:#22272E;line-height:1.4;')}>{resumeLine(p)}</span>
      <button
        type="button"
        onClick={() => {
          setGone(true);
          navigate(p.to);
        }}
        style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;border:0;background:#2F5C97;color:#fff;font-family:inherit;font-size:16px;font-weight:700;white-space:nowrap;cursor:pointer;')}
      >
        Go back
      </button>
      <button
        type="button"
        onClick={() => setGone(true)}
        style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);background:transparent;color:#22272E;font-family:inherit;font-size:16px;font-weight:700;white-space:nowrap;cursor:pointer;')}
      >
        Not now
      </button>
    </div>
  );
}
